import { useState } from 'react';
import {
  facilityChecklist, facilityCoverage, notifPrefs, notifPrefsSave, overviewSurvey, surveyExport, surveyLinkCreate, surveyLinkRevoke, surveyLinks, teamAccessLog, teamAccessRequests,
  type ApiError, type FacilityCoverage, type NotifPrefs, type Role, type SurveyExport, type SurveyLinkMade, type SurveyPart,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { DevNote } from '../../ui/buildLabels';
import { ErrLine, I, pre, sx, useApi, useGuarded } from './kit';
import { BreakGlassRequestCard } from '../residents/TeamAccess';
import { BuildingLoading, PickOneBuilding, useBuilding } from './building';
import { LanguageRow } from '../../i18n/LanguageRow';

// Console V6's new Settings sections: Break-glass (the log), Channels (how Atlas reaches
// you), Pilot go-live (Facility Admin) and Surveyor export (Facility Admin). Each reads the
// routes the server already serves; what V6 shows that no route answers is said plainly.

const WRAP = 'box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;display:flex;flex-direction:column;gap:10px;';
const HEAD = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const dot = (c: string) => 'width:7px;height:7px;border-radius:50%;background:' + c + ';flex:0 0 7px;';
const hm = (iso: string | number) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const day = (iso: string | number) => new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });

function Head({ color, children }: { color: string; children: string }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;')}>
      <span style={sx(dot(color))} />
      <span style={sx('flex:1 1 0;min-width:0;' + HEAD)}>{children}</span>
    </div>
  );
}

// ── Break-glass ───────────────────────────────────────────────────────────────

/**
 * Break-glass (design seIsBG): every request, approval, emergency start and view of a
 * resident's record by Aithora staff, newest first, append-only. From the server's requests
 * and its view log (team-access).
 */
export function BreakGlassSection() {
  const { session, client } = useSession();
  const { role } = useConsoleRole();
  const orgId = session?.user.organizationId ?? undefined;
  const reqs = useApi((c, s) => teamAccessRequests(c, { signal: s }), []);
  // The break-glass read log is for the building's admin and the owner (server rule); an Operator
  // sees the access requests only, rather than a refused read (crawl 2026-10-13).
  const canLog = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const log = useApi((c, s) => (canLog ? teamAccessLog(c, { query: orgId ? { organizationId: orgId } : {}, signal: s }) : Promise.resolve(null)), [orgId, canLog]);
  if ((reqs.loading && !reqs.data) || (log.loading && !log.data)) return <Skeleton rows={4} label="Loading the break-glass log" />;
  const rows: { at: number; what: string; who: string }[] = [];
  for (const r of reqs.data?.requests ?? []) {
    const by = r.requesterName ?? 'Aithora';
    const why = r.details ?? r.reason;
    if (r.requestedAt) rows.push({ at: Date.parse(r.requestedAt), what: (r.emergency ? 'Emergency access started: ' : 'Requested access: ') + why, who: by + (r.ticket ? ' · ' + r.ticket : '') + ' · ' + r.hours + 'h' });
    if (r.decidedAt && (r.status === 'approved' || r.status === 'denied') && !r.emergency) rows.push({ at: Date.parse(r.decidedAt), what: r.status === 'approved' ? 'Approved for ' + r.hours + 'h' : 'Denied', who: 'The facility admin' });
    if (r.reviewedAt) rows.push({ at: Date.parse(r.reviewedAt), what: 'Emergency access reviewed', who: 'The facility admin' + (r.reviewNote ? ' · ' + r.reviewNote : '') });
    if (r.status === 'ended' && r.endsAt) rows.push({ at: Date.parse(r.endsAt), what: 'Access ended early', who: by });
    if (r.status === 'expired' && r.endsAt) rows.push({ at: Date.parse(r.endsAt), what: 'Access ended on its own', who: 'Time ran out' });
  }
  for (const v of log.data?.views ?? []) rows.push({ at: Date.parse(v.at), what: 'Opened ' + v.resource, who: v.by + (v.ticket ? ' · ' + v.ticket : '') });
  rows.sort((a, b) => b.at - a.at);
  return (
    <div data-screen-label="Break-glass log" data-testid="break-glass-log" style={sx(WRAP)}>
      {role === 'ADMIN' && <BreakGlassRequestCard client={client} />}
      <Head color="#8A5A00">{'Break-glass · every time Aithora staff saw a resident’s record'}</Head>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;text-wrap:pretty;')}>
        Aithora staff see no resident record unless you approve, for 1, 4 or 8 hours. In an emergency access starts at once and you review it within 1 business day. This log is append-only.
      </div>
      {rows.map((g, i) => (
        <div key={i} style={sx('display:grid;grid-template-columns:90px minmax(0,1fr);gap:4px 14px;padding:9px 0;border-top:1px solid rgba(34,39,46,.08);')}>
          <span style={sx('font-size:16px;font-weight:700;color:#54606C;font-variant-numeric:tabular-nums;')}>{day(g.at) + ' ' + hm(g.at)}</span>
          <span style={sx('min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:600;color:#22272E;')}>{I(g.what)}</span>
            <span style={sx('display:block;font-size:16px;color:#54606C;')}>{I(g.who)}</span>
          </span>
        </div>
      ))}
      {rows.length === 0 && !reqs.error && !log.error && <div style={sx('font-size:16px;color:#54606C;')}>No break-glass access yet. Every request, approval, view and end lands here.</div>}
      <ErrLine error={reqs.error ?? log.error} />
    </div>
  );
}

// ── Channels ──────────────────────────────────────────────────────────────────

const STAFF_ROLES: Role[] = ['SUPER_ADMIN', 'OPERATOR', 'ADMIN', 'CAREGIVER', 'STAFF', 'DEVELOPER', 'SALES', 'PARTNER'];
const chipS = (c: string, b: string) => 'display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;white-space:nowrap;color:' + c + ';background:' + b + ';';
const SEG = (on: boolean) => 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (on ? 'color:#fff;background:#264C7E;' : 'color:#3C454F;border:1px solid rgba(34,39,46,.16);');

/**
 * Channels (design seIsCh, chLayer): how Atlas reaches this person. In the app is always on;
 * push and the text backup are the person's own settings (GET/PUT /settings/notifications/prefs);
 * WhatsApp and iPhone critical alerts wait on Meta and Apple; voice calls for an unanswered
 * urgent alert are part of the alert ladder and have no per-person switch on the server yet.
 * The language choice the design puts here belongs to the console's language switch.
 */
export function ChannelsSection() {
  const { client } = useSession();
  const { role } = useConsoleRole();
  const { canWrite, roReason } = useGuarded();
  const staff = !!role && STAFF_ROLES.includes(role);
  const q = useApi((c, s) => notifPrefs(c, { signal: s }), []);
  const [p, setP] = useState<NotifPrefs | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const prefs = p ?? q.data;
  if (!prefs && !q.error) return <Skeleton rows={5} label="Loading your channels" />;
  const save = async (patch: Partial<Pick<NotifPrefs, 'quietHours' | 'pushEnabled' | 'textBackup'>>) => {
    if (!canWrite) return;
    setErr(null);
    try {
      setP(await notifPrefsSave(client, { body: patch }));
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const push = prefs?.pushEnabled ?? true;
  const sms = prefs?.textBackup ?? false;
  type Row = { key: string; name: string; st: string; stStyle: string; carries: string; need: string; on: boolean; locked: boolean; toggle?: () => void };
  const okChip = chipS('#12503C', 'rgba(47,138,111,.16)');
  const offChip = chipS('#54606C', 'rgba(34,39,46,.08)');
  const rows: Row[] = [
    { key: 'app', name: 'In the app', st: 'ALWAYS ON', stStyle: okChip, carries: 'Everything, with full details. Always on: it is where every alert lives.', need: '', on: true, locked: true },
    { key: 'push', name: 'Push notifications', st: push ? 'ON' : 'OFF', stStyle: push ? okChip : offChip, carries: 'Says what needs you, never a resident’s name on the lock screen.', need: '', on: push, locked: !prefs, toggle: () => void save({ pushEnabled: !push }) },
    { key: 'sms', name: 'Text message', st: sms ? 'ON' : 'OFF', stStyle: sms ? okChip : offChip, carries: 'Only “Something needs you. Open Aithora.” No names, no health details.', need: sms ? '' : 'Your consent. Turn it on and we text a code to confirm your number.', on: sms, locked: !prefs, toggle: () => void save({ textBackup: !sms }) },
    { key: 'wa', name: 'WhatsApp', st: 'NOT STARTED', stStyle: offChip, carries: 'Same as text: a nudge to open Aithora, nothing private.', need: 'Not set up yet. Needs Meta approval of our business account and message templates.', on: false, locked: true },
    ...(staff
      ? [
          { key: 'crit', name: 'Critical alerts on iPhone', st: 'WAITING ON APPLE', stStyle: chipS('#8A5A00', 'rgba(138,90,0,.12)'), carries: 'Lets an emergency sound even when the phone is on silent or Do Not Disturb.', need: 'Waiting on Apple. Aithora has requested the critical-alerts permission. Until it is granted, staff keep their ringer on during shifts.', on: false, locked: true },
          { key: 'voice', name: 'Voice call', st: 'ON', stStyle: okChip, carries: 'Calls your phone only for an urgent alert nobody has answered.', need: '', on: true, locked: true },
        ]
      : []),
  ];
  const quiet = prefs?.quietHours ?? false;
  const quietLabel = prefs ? `${prefs.quietFrom} – ${prefs.quietTo}` : 'Quiet hours';
  const order = ['1 · In the app', '2 · Push'].concat(sms ? ['3 · Text message'] : []).concat(staff ? [(sms ? '4' : '3') + ' · Voice call, urgent only'] : []);
  const wait = prefs?.smsFallbackMinutes ?? 2;
  return (
    <div data-screen-label="Channels" data-testid="channels-section" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;display:flex;flex-direction:column;gap:12px;')}>
      <Head color="#2F5C97">{'Channels · how Atlas reaches you'}</Head>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;text-wrap:pretty;')}>
        {staff
          ? 'Atlas reaches you in the app first. If you don’t see it, it tries the next channel you’ve turned on. Urgent alerts always come through, even in quiet hours.'
          : 'Atlas and the care team reach you in the app first. Turn on the other ways you’d like to hear from us.'}
      </div>
      <LanguageRow />
      <div style={sx('display:flex;flex-direction:column;')}>
        {rows.map((c) => (
          <div key={c.key} style={sx('display:flex;flex-wrap:wrap;align-items:center;gap:8px 14px;padding:11px 0;border-top:1px solid rgba(34,39,46,.08);')}>
            <span style={sx('flex:1 1 260px;min-width:0;')}>
              <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{c.name}</span>
                <span style={sx(c.stStyle)}>{c.st}</span>
              </span>
              <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:3px;line-height:1.45;text-wrap:pretty;')}>{c.carries}</span>
              {c.need && (
                <span style={sx('display:block;font-size:16px;color:#22272E;margin-top:3px;')}>
                  <b>Needed:</b> {c.need}
                </span>
              )}
              {c.key === 'voice' && (
                <DevNote>
                  <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:3px;')}>No per-person voice switch on the server yet; voice calls follow the alert ladder.</span>
                </DevNote>
              )}
            </span>
            <a
              href="#"
              role="switch"
              aria-checked={c.on}
              aria-label={c.name + (c.on ? ' on' : ' off')}
              aria-disabled={c.locked || !canWrite || undefined}
              title={!c.locked && !canWrite ? (roReason ?? undefined) : undefined}
              onClick={(e) => {
                pre(e);
                if (!c.locked && canWrite) c.toggle?.();
              }}
              style={sx(
                'display:inline-flex;align-items:center;width:42px;height:24px;flex:0 0 42px;border-radius:14px;padding:2px;box-sizing:border-box;text-decoration:none;' +
                  (c.locked ? 'opacity:.55;cursor:not-allowed;' : '') +
                  (c.on ? 'background:#1F6B52;justify-content:flex-end;' : 'background:rgba(34,39,46,.22);justify-content:flex-start;'),
              )}
            >
              <span style={sx('display:block;width:20px;height:20px;border-radius:50%;background:#FDFDFD;box-shadow:0 1px 3px rgba(34,39,46,.28);')} />
            </a>
          </div>
        ))}
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:10px 18px;')}>
        <div style={sx('display:flex;flex-direction:column;gap:6px;padding:10px 12px;border:1px solid rgba(34,39,46,.12);border-radius:10px;')}>
          <span style={sx(HEAD)}>{'If one doesn’t get through'}</span>
          {order.map((o) => (
            <span key={o} style={sx('font-size:16px;color:#22272E;')}>{o}</span>
          ))}
          <span style={sx('font-size:16px;color:#54606C;')}>{`Each step waits ${wait} minutes for a read or a reply.`}</span>
        </div>
        <div style={sx('display:flex;flex-direction:column;gap:6px;padding:10px 12px;border:1px solid rgba(34,39,46,.12);border-radius:10px;')}>
          <span style={sx(HEAD)}>Quiet hours</span>
          <span style={sx('display:flex;gap:4px;flex-wrap:wrap;')}>
            {([[true, quietLabel], [false, 'None']] as [boolean, string][]).map(([v, label]) => (
              <a key={label} href="#" aria-pressed={quiet === v} aria-disabled={!prefs || !canWrite || undefined} onClick={(e) => { pre(e); if (prefs && canWrite && quiet !== v) void save({ quietHours: v }); }} style={sx(SEG(quiet === v))}>
                {label}
              </a>
            ))}
          </span>
          <span style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>
            {!quiet ? 'Everything comes through at any hour.' : staff ? 'Nothing but urgent alerts in your quiet hours.' : `Only urgent news about your person between ${prefs?.quietFrom ?? ''} and ${prefs?.quietTo ?? ''}.`}
          </span>
        </div>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;')}>
        Text and WhatsApp never carry a resident’s name or health details; they say “Something needs you. Open Aithora.” Reply STOP to any text to turn texts off. Every change is logged with who and when.
      </div>
      <ErrLine error={err ?? q.error} />
    </div>
  );
}

// ── Pilot go-live ─────────────────────────────────────────────────────────────

const PILL = (k: 'ok' | 'wait' | 'need') =>
  'flex:0 0 auto;min-width:64px;text-align:center;font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:5px;padding:3px 7px;' +
  (k === 'ok' ? 'color:#12503C;background:rgba(47,138,111,.16);' : k === 'wait' ? 'color:#8A5A00;background:rgba(138,90,0,.12);' : 'color:#B3261E;background:rgba(179,38,30,.1);');

/**
 * Pilot go-live (design seIsPilot): everything that must be true before the building goes
 * live, each line checking itself. From the building's onboarding checklist
 * (GET /onboarding/facility/:id): done is READY, Aithora's own open step is WAITING, the
 * building's open step is TO DO. Nothing here is ticked by hand. A Facility Admin's own
 * building; Super Admin and Operator the building picked in the facility switcher.
 */
export function PilotSection() {
  const building = useBuilding();
  if (building.mode === 'loading') return <BuildingLoading />;
  if (!building.orgId) return <PickOneBuilding what="Pilot go-live" />;
  return <PilotChecklist key={building.orgId} orgId={building.orgId} name={building.name} />;
}

function PilotChecklist({ orgId, name }: { orgId: string; name: string | null }) {
  const q = useApi((c, s) => facilityChecklist(c, { params: { id: orgId }, signal: s }), [orgId]);
  if (!q.data && !q.error) return <Skeleton rows={6} label="Loading the go-live checklist" />;
  const items = q.data?.items ?? [];
  const ready = items.filter((i) => i.done).length;
  return (
    <div data-screen-label="Pilot go-live" data-testid="pilot-golive" style={sx(WRAP)}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;')}>
        <span style={sx(dot('#2F5C97'))} />
        <span style={sx(HEAD)}>{'Pilot go-live · ' + (q.data?.facility.name ?? name ?? 'this building')}</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;')}>
        <b style={sx('color:#22272E;')}>{q.data ? `${ready} of ${items.length} ready` : 'Not available yet'}.</b> Everything that must be true before residents and staff go live. Each line checks itself; nothing here is ticked by hand.
      </div>
      {items.map((p) => {
        const k: 'ok' | 'wait' | 'need' = p.done ? 'ok' : p.owner === 'aithora' ? 'wait' : 'need';
        return (
          <div key={p.key} style={sx('display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);')}>
            <span style={sx(PILL(k))}>{k === 'ok' ? 'READY' : k === 'wait' ? 'WAITING' : 'TO DO'}</span>
            <span style={sx('flex:1 1 280px;min-width:0;')}>
              <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{p.title}</span>
              <span style={sx('display:block;font-size:16px;color:#54606C;line-height:1.45;')}>{p.done ? (p.evidence ?? (p.doneAt ? 'Done ' + day(p.doneAt) + '.' : p.why)) : k === 'wait' ? 'Waiting on Aithora. ' + p.why : p.why}</span>
            </span>
          </div>
        );
      })}
      <ErrLine error={q.error} />
    </div>
  );
}

// ── Surveyor export ───────────────────────────────────────────────────────────

const RANGES: ['7d' | '30d' | '90d' | 'survey', string][] = [['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['90d', 'Last 90 days'], ['survey', 'Since last survey']];
/** The design's parts, its order and its defaults (svParts: Guardian, Ledger and Break-glass on). */
const PARTS: [SurveyPart, string, string, boolean][] = [
  ['guardian', 'Guardian timeline', 'Every alert, who answered, and how fast', true],
  ['ledger', 'Truth Ledger', 'Every action, with who, when and the ledger id', true],
  ['breakglass', 'Break-glass log', 'Every time Aithora staff saw a resident record', true],
  ['medpass', 'Med pass record', 'Given, not given with reasons, struck out', false],
  ['staffing', 'Staffing by shift', 'Who was on, and when the ladder fired', false],
];

/**
 * V9's sixth part, Pulled-away coverage (design svParts 'c', on by default): the line under it is
 * the building's real figure, "covered on time" over the last 30 days from
 * GET /facilities/:id/worklist/coverage. A figure that cannot be read says so, never a number.
 */
export function coverageSub(c: FacilityCoverage | null | undefined, failed: boolean): string {
  const tail = ' · every moved task, who took it and when it was given';
  if (!c) return 'Covered on time: ' + (failed ? 'not connected yet' : 'loading') + tail;
  return 'Covered on time: ' + (c.coveredOnTime.pct === null ? 'nothing covered yet' : c.coveredOnTime.pct + '%') + tail;
}

function save(name: string, blob: Blob): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

/** The files of one export: the PDF and the CSV (the design's), and the signature to check them by. */
export function surveyFiles(out: SurveyExport): { name: string; blob: Blob }[] {
  const stem = 'aithora-surveyor-export-' + out.facility.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-' + out.generatedAt.slice(0, 10);
  const bin = atob(out.pdfBase64);
  const pdf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) pdf[i] = bin.charCodeAt(i);
  return [
    { name: stem + '.pdf', blob: new Blob([pdf], { type: 'application/pdf' }) },
    { name: stem + '.csv', blob: new Blob([out.csv], { type: 'text/csv' }) },
    { name: stem + '.signature.json', blob: new Blob([JSON.stringify({ manifest: out.manifest, manifestJws: out.manifestJws, ledger: out.ledger }, null, 2)], { type: 'application/json' }) },
  ];
}

/**
 * Surveyor export (design seIsSurvey): one signed, dated file for a state inspection, for this
 * building and a date range (POST /exports/survey). Every part the design lists comes from its
 * own record: Guardian timeline (emergencies, who claimed each and how fast), the Truth Ledger
 * (the signed range export), the break-glass log, the med pass record (struck doses marked) and
 * staffing by shift (who was on, when the ladder fired). A part with nothing recorded says so in
 * the file. "Since last survey" starts at the CMS survey date for the building's CCN; the server
 * says when there is none. Resident names only for your own building. The export is a PHI read
 * and is logged on the Truth Ledger.
 *
 * How it reaches the surveyor (research 2026-10-13, section 3): download it here, on a facility
 * computer, or make a secure link that expires in 72 hours (POST /exports/survey/link), copy it
 * and give it to the surveyor. Every download through a link is logged with the time and the
 * address, and a link can be revoked. Never by email.
 */
export function SurveyorSection() {
  const building = useBuilding();
  if (building.mode === 'loading') return <BuildingLoading />;
  if (!building.orgId) return <PickOneBuilding what="Surveyor export" />;
  return <SurveyorExport key={building.orgId} orgId={building.orgId} />;
}

function SurveyorExport({ orgId }: { orgId: string }) {
  const { client } = useSession();
  const { canWrite, roReason } = useGuarded();
  const [range, setRange] = useState<(typeof RANGES)[number][0]>('30d');
  const [parts, setParts] = useState<Record<SurveyPart, boolean>>(() => Object.fromEntries(PARTS.map(([k, , , on]) => [k, on])) as Record<SurveyPart, boolean>);
  const [done, setDone] = useState<SurveyExport | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [made, setMade] = useState<SurveyLinkMade | null>(null);
  const [copied, setCopied] = useState(false);
  const { role } = useConsoleRole();
  // A link is made and revoked only by the building's own Facility Admin (the server refuses anyone else).
  const linkAdmin = role === 'ADMIN';
  const links = useApi((c, s) => surveyLinks(c, { query: { facility: orgId }, signal: s }), [orgId], linkAdmin);
  const picked = PARTS.filter(([k]) => parts[k]).map(([k]) => k);
  // Pulled-away coverage: on by default, as the design has it. Its figure is real; the signed
  // export (POST /exports/survey) does not carry the part yet, so the screen says so.
  const cover = useApi((c, s) => facilityCoverage(c, { params: { id: orgId }, signal: s }), [orgId]);
  const [withCover, setWithCover] = useState(true);
  // "Since last survey" starts at the CMS survey date for the building: with none on file the
  // range is not offered as pressable, and the buttons say why instead of failing.
  const survey = useApi((c, s) => overviewSurvey(c, { query: { facility: orgId }, signal: s }), [orgId]);
  const surveyDate = survey.data?.lastStandardSurvey ?? null;
  const noSurveyDate = !!survey.data && !surveyDate;
  const NO_DATE = 'No CMS survey date is on file for this building, so Since last survey has no start. Pick 7, 30 or 90 days.';
  const blocked = !canWrite ? (roReason ?? undefined) : !picked.length ? 'Pick at least one part' : range === 'survey' && noSurveyDate ? NO_DATE : undefined;
  const makeLink = async () => {
    if (!canWrite || !linkAdmin || busy || !picked.length || blocked) return;
    setBusy(true);
    setErr(null);
    setCopied(false);
    try {
      setMade(await surveyLinkCreate(client, { body: { range, parts: picked } }));
      links.reload();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const revoke = async (id: string) => {
    if (!canWrite || !linkAdmin) return;
    setErr(null);
    try {
      await surveyLinkRevoke(client, { params: { id } });
      if (made?.id === id) setMade(null);
      links.reload();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const go = async () => {
    if (!canWrite || busy || !picked.length || blocked) return;
    setBusy(true);
    setErr(null);
    try {
      const out = await surveyExport(client, { body: { facility: orgId, range, parts: picked } });
      for (const f of surveyFiles(out)) save(f.name, f.blob);
      setDone(out);
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-screen-label="Surveyor export" data-testid="surveyor-export" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;display:flex;flex-direction:column;gap:12px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;')}>
        <span style={sx(dot('#2F5C97'))} />
        <span style={sx(HEAD)}>{'Surveyor export · for a state inspection'}</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;')}>One file for the surveyor: what happened, who did what, and when, straight from the Truth Ledger. Nothing in it can be edited after export.</div>
      <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
        <span style={sx('font-size:16px;font-weight:600;color:#3C454F;margin-right:4px;')}>Dates</span>
        {RANGES.map(([k, label]) =>
          k === 'survey' && noSurveyDate ? (
            <span key={k} data-testid="survey-range-survey" aria-disabled="true" title={NO_DATE} style={sx(SEG(false) + 'opacity:.5;cursor:default;')}>
              {label}
            </span>
          ) : (
            <a key={k} href="#" aria-pressed={range === k} title={k === 'survey' && surveyDate ? 'From ' + surveyDate : undefined} onClick={(e) => { pre(e); setRange(k); setDone(null); }} style={sx(SEG(range === k))}>
              {label}
            </a>
          ),
        )}
        {noSurveyDate && <span data-testid="survey-no-date" style={sx('font-size:16px;color:#54606C;')}>No CMS survey date on file, so Since last survey is not available.</span>}
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:8px 18px;')}>
        {PARTS.map(([k, name, sub]) => (
          <a key={k} href="#" role="checkbox" aria-checked={parts[k]} data-testid={`survey-part-${k}`} onClick={(e) => { pre(e); setParts((p) => ({ ...p, [k]: !p[k] })); setDone(null); }}
            style={sx('display:flex;align-items:flex-start;gap:10px;padding:10px 12px;border-radius:10px;border:1px solid rgba(34,39,46,.14);text-decoration:none;color:inherit;')}>
            <span style={sx('flex:0 0 20px;width:20px;height:20px;border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:800;' + (parts[k] ? 'background:#264C7E;color:#fff;' : 'border:1px solid rgba(34,39,46,.3);color:transparent;'))}>{parts[k] ? '✓' : ''}</span>
            <span style={sx('min-width:0;')}>
              <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{name}</span>
              <span style={sx('display:block;font-size:16px;color:#54606C;line-height:1.4;')}>{sub}</span>
            </span>
          </a>
        ))}
        <a href="#" role="checkbox" aria-checked={withCover} data-testid="survey-part-coverage" onClick={(e) => { pre(e); setWithCover((v) => !v); setDone(null); }}
          style={sx('display:flex;align-items:flex-start;gap:10px;padding:10px 12px;border-radius:10px;border:1px solid rgba(34,39,46,.14);text-decoration:none;color:inherit;')}>
          <span style={sx('flex:0 0 20px;width:20px;height:20px;border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:800;' + (withCover ? 'background:#264C7E;color:#fff;' : 'border:1px solid rgba(34,39,46,.3);color:transparent;'))}>{withCover ? '✓' : ''}</span>
          <span style={sx('min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>Pulled-away coverage</span>
            <span data-testid="survey-coverage-sub" style={sx('display:block;font-size:16px;color:#54606C;line-height:1.4;')}>{coverageSub(cover.data, !!cover.error)}</span>
          </span>
        </a>
      </div>
      {withCover && (
        <div data-testid="survey-coverage-note" style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;')}>
          Pulled-away coverage is not in the exported file yet. It joins the file once Aithora adds it; the figure above is the last 30 days.
        </div>
      )}
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;')}>
        <a href="#" data-testid="survey-go" aria-disabled={!!blocked || busy || undefined} title={blocked} onClick={(e) => { pre(e); void go(); }} style={sx('display:inline-flex;align-items:center;height:34px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#264C7E;' + (blocked ? 'opacity:.5;cursor:default;' : ''))}>
          {done ? 'Downloaded' : busy ? 'Working' : 'Download PDF + CSV'}
        </a>
        {linkAdmin && (
          <a href="#" data-testid="survey-link-go" aria-disabled={!!blocked || busy || undefined} title={blocked} onClick={(e) => { pre(e); void makeLink(); }} style={sx('display:inline-flex;align-items:center;height:34px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(38,76,126,.4);' + (blocked ? 'opacity:.5;cursor:default;' : ''))}>
            Create secure link for the surveyor (expires in 72 hours)
          </a>
        )}
        <span data-testid="survey-note" style={sx('font-size:16px;color:#54606C;')}>{done ? 'Saved ' + hm(done.generatedAt) + ' · the export itself is logged on the Truth Ledger (' + done.ledgerRef + ').' : 'Signed and dated. Resident names are included only for your building.'}</span>
      </div>
      {done ? (
        <div data-testid="survey-summary" style={sx('display:flex;flex-direction:column;gap:4px;font-size:16px;color:#3C454F;')}>
          {PARTS.filter(([k]) => done.parts[k]).map(([k, name]) => {
            const p = done.parts[k]!;
            return <span key={k}>{name + ': ' + (p.empty ? p.empty : `${p.count} ${p.count === 1 ? 'row' : 'rows'}${p.truncated ? ', the first ' + p.rows.length + ' in the file' : ''}`)}</span>;
          })}
        </div>
      ) : null}
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;')}>
        Download on a facility computer, or give the surveyor a secure link. Do not email the file or send it from a phone.
      </div>
      {made && (
        <div data-testid="survey-link-made" style={sx('display:flex;flex-direction:column;gap:6px;padding:10px 12px;border:1px solid rgba(38,76,126,.3);border-radius:10px;background:rgba(79,138,214,.06);')}>
          <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{'Secure link · expires ' + day(made.expiresAt) + ' ' + hm(made.expiresAt)}</span>
          <span style={sx('display:flex;gap:8px;flex-wrap:wrap;align-items:center;')}>
            <input readOnly aria-label="Secure link" value={made.url} onFocus={(e) => e.currentTarget.select()} style={sx('flex:1 1 320px;min-width:0;box-sizing:border-box;height:30px;padding:0 9px;border-radius:7px;border:1px solid rgba(34,39,46,.22);background:#FCFAF4;color:#22272E;font-size:16px;font-family:inherit;')} />
            <a href="#" data-testid="survey-link-copy" onClick={(e) => { pre(e); void copy(made.url); }} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;color:#fff;background:#264C7E;')}>
              {copied ? 'Copied' : 'Copy link'}
            </a>
          </span>
          <span style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>Shown once: Aithora keeps only a fingerprint of it. Give it to the surveyor in person or read it out; every download is logged with the time and the address.</span>
        </div>
      )}
      {linkAdmin && (
        <div data-testid="survey-links" style={sx('display:flex;flex-direction:column;gap:4px;')}>
          <span style={sx(HEAD)}>Active secure links</span>
          {(links.data?.links ?? []).length === 0 && !links.error && <span style={sx('font-size:16px;color:#54606C;')}>{links.data ? 'None. A link you make shows here until it expires or you revoke it.' : 'Loading'}</span>}
          {(links.data?.links ?? []).map((l) => (
            <div key={l.id} data-testid={`survey-link-${l.id}`} style={sx('display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:8px 0;border-top:1px solid rgba(34,39,46,.08);')}>
              <span style={sx('flex:1 1 300px;min-width:0;')}>
                <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{l.range.label + ' · ' + l.parts.map((p) => PARTS.find(([k]) => k === p)?.[1] ?? p).join(', ')}</span>
                <span style={sx('display:block;font-size:16px;color:#54606C;')}>
                  {(l.createdBy ? 'Made by ' + l.createdBy + ' · ' : '') + (l.expiresAt ? 'expires ' + day(l.expiresAt) + ' ' + hm(l.expiresAt) : '') + ' · ' + l.downloads + (l.downloads === 1 ? ' download' : ' downloads')}
                </span>
              </span>
              <a href="#" aria-label="Revoke this link" aria-disabled={!canWrite || undefined} title={!canWrite ? (roReason ?? undefined) : undefined} onClick={(e) => { pre(e); void revoke(l.id); }} style={sx('display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;color:#B3261E;border:1px solid rgba(179,38,30,.35);')}>
                Revoke
              </a>
            </div>
          ))}
          <ErrLine error={links.error} />
        </div>
      )}
      <ErrLine error={err} />
    </div>
  );
}
